// Pure model + logic for the S4 routing editor (spec 01 §4 S4, §2.11, §3.5).
//
// Free of React and network so it can be exhaustively unit-tested (doctrine 1:
// green units are necessary). The screen + drawer components in this folder
// consume these helpers; the api-client is the only data source.

import { normalizeLocale, SUPPORTED_LOCALES, type Locale } from "@assessly/contracts/locales";
import type { components } from "@assessly/api-client";

export type RoutingStep = components["schemas"]["RoutingStep"];
export type RoutingStepInput = components["schemas"]["RoutingStepInput"];
export type Routing = components["schemas"]["Routing"];
export type RoutingSummary = components["schemas"]["RoutingSummary"];
export type WorkCenter = components["schemas"]["WorkCenter"];
export type BomLine = components["schemas"]["BomLine"];
export type RunBasis = RoutingStep["run_basis"];
export type RoutingStatus = Routing["status"];

/** The lead-time preview is quoted per this many base-UoM units (cable: metres). */
export const LEAD_TIME_BASIS_QTY = 1000;

/** Steps are renumbered on this stride so intermediate inserts stay possible. */
export const STEP_STRIDE = 10;

/** The product locales, from the registry (D-430). */
export const LOCALES = SUPPORTED_LOCALES;
export type { Locale };

// --- Instructions rich-block document (routing_steps.instructions jsonb) -------
//
// The floor PWA (04/S9) renders these blocks. We manage a `blocks` array of text
// + image-reference blocks and round-trip any other keys already on the jsonb
// untouched (`extra`) so we never silently drop data written by another surface.

export interface TextBlock {
  readonly type: "text";
  readonly text: string;
}
export interface ImageBlock {
  readonly type: "image";
  readonly s3_key: string;
  readonly alt: string;
}
export type InstructionBlock = TextBlock | ImageBlock;

export interface InstructionsDoc {
  readonly blocks: InstructionBlock[];
  /** Any keys already on the jsonb that are not `blocks`, preserved on save. */
  readonly extra: Record<string, unknown>;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function parseBlock(raw: unknown): InstructionBlock | null {
  const rec = asRecord(raw);
  if (rec === null) return null;
  if (rec["type"] === "image") {
    const key = rec["s3_key"];
    const alt = rec["alt"];
    return {
      type: "image",
      s3_key: typeof key === "string" ? key : "",
      alt: typeof alt === "string" ? alt : "",
    };
  }
  // Any non-image block is treated as a text block (the default kind).
  const text = rec["text"];
  return { type: "text", text: typeof text === "string" ? text : "" };
}

export function parseInstructions(raw: Record<string, unknown>): InstructionsDoc {
  const rawBlocks = raw["blocks"];
  const blocks: InstructionBlock[] = Array.isArray(rawBlocks)
    ? rawBlocks.map(parseBlock).filter((b): b is InstructionBlock => b !== null)
    : [];
  const extra: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (k !== "blocks") extra[k] = v;
  }
  return { blocks, extra };
}

export function serializeInstructions(doc: InstructionsDoc): Record<string, unknown> {
  return { ...doc.extra, blocks: doc.blocks.map((b) => ({ ...b })) };
}

export function instructionsSummary(doc: InstructionsDoc): { text: number; image: number } {
  let text = 0;
  let image = 0;
  for (const b of doc.blocks) {
    if (b.type === "image") image += 1;
    else text += 1;
  }
  return { text, image };
}

// --- Working step draft --------------------------------------------------------
//
// `_key` is a stable client id for React lists (server step ids are dropped on
// the PUT /steps replace, so we cannot key on them across reorders).

export interface StepDraft {
  readonly _key: string;
  step_no: number;
  name: string;
  work_center_id: string;
  alternate_work_center_ids: string[];
  setup_minutes: number;
  run_basis: RunBasis;
  run_minutes: number;
  batch_qty: number | null;
  setup_key: string | null;
  operation_scrap_pct: number;
  qc_control_point_id: string | null;
  requires_measured_capture: boolean;
  requires_serial_capture: boolean;
  backflush: boolean;
  is_outsourced: boolean;
  outsource_code: string | null;
  min_crew: number;
  can_overlap: boolean;
  instructions: InstructionsDoc;
}

export function newKey(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `k-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

export function stepFromServer(s: RoutingStep): StepDraft {
  return {
    _key: newKey(),
    step_no: s.step_no,
    name: s.name,
    work_center_id: s.work_center_id,
    alternate_work_center_ids: s.alternate_work_center_ids ?? [],
    setup_minutes: s.setup_minutes,
    run_basis: s.run_basis,
    run_minutes: s.run_minutes,
    batch_qty: s.batch_qty,
    setup_key: s.setup_key,
    operation_scrap_pct: s.operation_scrap_pct,
    qc_control_point_id: s.qc_control_point_id,
    requires_measured_capture: s.requires_measured_capture,
    requires_serial_capture: s.requires_serial_capture,
    backflush: s.backflush,
    is_outsourced: s.is_outsourced,
    outsource_code: s.outsource_code,
    min_crew: s.min_crew,
    can_overlap: s.can_overlap,
    instructions: parseInstructions(s.instructions),
  };
}

/** A blank step positioned after the current last step. */
export function emptyStep(afterStepNo: number): StepDraft {
  return {
    _key: newKey(),
    step_no: afterStepNo + STEP_STRIDE,
    name: "",
    work_center_id: "",
    alternate_work_center_ids: [],
    setup_minutes: 0,
    run_basis: "per_unit",
    run_minutes: 0,
    batch_qty: null,
    setup_key: null,
    operation_scrap_pct: 0,
    qc_control_point_id: null,
    requires_measured_capture: false,
    requires_serial_capture: false,
    backflush: true,
    is_outsourced: false,
    outsource_code: null,
    min_crew: 1,
    can_overlap: false,
    instructions: { blocks: [], extra: {} },
  };
}

/** Map a draft to the PUT /steps input; every field concrete (never `undefined`). */
export function stepToInput(s: StepDraft): RoutingStepInput {
  const perBatch = s.run_basis === "per_batch";
  const setupKey =
    s.setup_key !== null && s.setup_key.trim().length > 0 ? s.setup_key.trim() : null;
  const qc =
    s.qc_control_point_id !== null && s.qc_control_point_id.trim().length > 0
      ? s.qc_control_point_id.trim()
      : null;
  const outsource =
    s.is_outsourced && s.outsource_code !== null && s.outsource_code.trim().length > 0
      ? s.outsource_code.trim()
      : null;
  return {
    step_no: s.step_no,
    name: s.name.trim(),
    work_center_id: s.work_center_id,
    alternate_work_center_ids:
      s.alternate_work_center_ids.length > 0 ? [...s.alternate_work_center_ids] : null,
    setup_minutes: s.setup_minutes,
    run_basis: s.run_basis,
    run_minutes: s.run_minutes,
    batch_qty: perBatch ? s.batch_qty : null,
    setup_key: setupKey,
    operation_scrap_pct: s.operation_scrap_pct,
    qc_control_point_id: qc,
    requires_measured_capture: s.requires_measured_capture,
    requires_serial_capture: s.requires_serial_capture,
    backflush: s.backflush,
    is_outsourced: s.is_outsourced,
    outsource_code: outsource,
    min_crew: s.min_crew,
    can_overlap: s.can_overlap,
    instructions: serializeInstructions(s.instructions),
  };
}

// --- Reorder + renumber --------------------------------------------------------

export function moveItem<T>(arr: readonly T[], from: number, to: number): T[] {
  const next = [...arr];
  if (from < 0 || from >= next.length || to < 0 || to >= next.length || from === to) {
    return next;
  }
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return next;
  next.splice(to, 0, moved);
  return next;
}

/** Renumber step_no by the stride (10, 20, 30 …) after any reorder/insert/remove. */
export function renumberSteps(steps: readonly StepDraft[]): StepDraft[] {
  return steps.map((s, i) => ({ ...s, step_no: (i + 1) * STEP_STRIDE }));
}

// --- Lead-time preview ---------------------------------------------------------
//
// Estimate = Σ per step ( setup once + run for the basis quantity ).
//   per_unit  → run_minutes are per base unit (per metre) → × basisQty.
//   per_batch → run_minutes per batch of batch_qty → × (basisQty / batch_qty).
// A planning-free wall-time estimate (ignores overlap/crew — 03 does the real
// finite-capacity schedule); shown only as a design-time sanity figure.

export interface StepTiming {
  readonly setup_minutes: number;
  readonly run_basis: RunBasis;
  readonly run_minutes: number;
  readonly batch_qty: number | null;
}

export function stepRunMinutesForBasisQty(step: StepTiming, basisQty: number): number {
  if (step.run_basis === "per_batch") {
    const batch = step.batch_qty;
    if (batch === null || batch <= 0) return 0;
    return step.run_minutes * (basisQty / batch);
  }
  return step.run_minutes * basisQty;
}

export function computeLeadTimeMinutes(
  steps: readonly StepTiming[],
  basisQty: number = LEAD_TIME_BASIS_QTY,
): number {
  let total = 0;
  for (const s of steps) {
    total += s.setup_minutes + stepRunMinutesForBasisQty(s, basisQty);
  }
  return total;
}

// --- BOM consume-at-step cross-check (spec 01 §3.5, S4 warning chip) -----------

/**
 * Distinct BOM `consume_at_step_no` values that do not match any current routing
 * step number — the S4 warning chip. NULL means "at WO start" and is ignored.
 */
export function missingConsumeSteps(
  bomLines: readonly Pick<BomLine, "consume_at_step_no">[],
  stepNos: ReadonlySet<number>,
): number[] {
  const missing = new Set<number>();
  for (const line of bomLines) {
    const at = line.consume_at_step_no;
    if (at !== null && !stepNos.has(at)) missing.add(at);
  }
  return [...missing].sort((a, b) => a - b);
}

// --- Work-center display -------------------------------------------------------

/**
 * A work centre's name in the reader's locale, else English, else any locale
 * it has, else its code. A plant-map commit stores only the tenant's locale
 * (spec 02), so a bundle can lack English entirely.
 */
export function workCenterLabel(wc: WorkCenter, locale: string): string {
  const names = wc.name_i18n;
  const pick = (key: Locale): string | undefined => {
    const name = names[key];
    return name !== undefined && name.trim().length > 0 ? name : undefined;
  };
  // `normalizeLocale` reads zh-TW as Traditional and never as Simplified.
  const own = normalizeLocale(locale);
  return (
    (own !== undefined ? pick(own) : undefined) ??
    pick("en") ??
    LOCALES.map(pick).find((name) => name !== undefined) ??
    wc.code
  );
}

// --- Setup-key autocomplete ----------------------------------------------------

/** Distinct non-empty setup keys already used across the steps (for the datalist). */
export function distinctSetupKeys(steps: readonly StepDraft[]): string[] {
  const keys = new Set<string>();
  for (const s of steps) {
    if (s.setup_key !== null && s.setup_key.trim().length > 0) keys.add(s.setup_key.trim());
  }
  return [...keys].sort((a, b) => a.localeCompare(b));
}

// --- Draft validation (mirrors the server's activation rules, spec §3.5) -------

export type StepFieldError =
  | "nameRequired"
  | "workCenterRequired"
  | "runMinutesInvalid"
  | "batchQtyRequired"
  | "outsourceCodeRequired";

export function validateStep(s: StepDraft): StepFieldError[] {
  const errs: StepFieldError[] = [];
  if (s.name.trim().length === 0) errs.push("nameRequired");
  if (s.work_center_id.length === 0) errs.push("workCenterRequired");
  if (!Number.isFinite(s.run_minutes) || s.run_minutes < 0) errs.push("runMinutesInvalid");
  if (s.run_basis === "per_batch" && (s.batch_qty === null || s.batch_qty <= 0))
    errs.push("batchQtyRequired");
  if (s.is_outsourced && (s.outsource_code === null || s.outsource_code.trim().length === 0))
    errs.push("outsourceCodeRequired");
  return errs;
}

/** Indices of steps that fail validation (for the "fix step N" save guard). */
export function invalidStepNumbers(steps: readonly StepDraft[]): number[] {
  return steps.filter((s) => validateStep(s).length > 0).map((s) => s.step_no);
}

export function isEditableStatus(status: RoutingStatus): boolean {
  return status === "draft";
}
